import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Language } from '@app/modules/personas/interfaces/persona-options.interface'
import { PromoCode } from '@app/modules/personas/interfaces/promo-code.interface'
import { PromoCodeService } from '@app/modules/personas/services/promo-code.service'
import { ToastrService } from '@app/services/toastr.service'
import { NbBadgeModule, NbButtonModule, NbCheckboxModule, NbDialogService, NbIconModule, NbInputModule, NbPopoverDirective, NbPopoverModule, NbTooltipModule } from '@nebular/theme'
import { CreatePromoCodeComponent } from '@personas/components/create-promo-code/create-promo-code.component'
import { Subject, takeUntil } from 'rxjs'
import { TagComponent } from '../tag/tag.component'

export interface TagItem {
  id?: number
  slug?: string
  name?: string
  code?: string
  active?: boolean
  disabled?: boolean
  separator?: boolean
  indent?: boolean
  alsoSelect?: any
  parent?: number
  right_to_left?: boolean
}

/**
 * Modern, redesigned tags component with better UX and logic
 */
@Component({
  selector: 'ngx-micetribe-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TagComponent, NbCheckboxModule, NbPopoverModule, NbTooltipModule, NbButtonModule, NbIconModule, NbBadgeModule, NbInputModule]
})
export class TagsComponent implements OnInit, OnChanges {
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective

  // Inputs
  @Input() availableItems: TagItem[] = []
  @Input() customLanguages: any[] = []
  @Input() currentItems: (number | string)[] = []
  @Input() emptyTitle = 'No items selected'
  @Input() noOptionsTitle = 'No options available'
  @Input() eventId = -1
  @Input() disabled = false
  @Input() listType: '' | 'Languages' | 'PromoCodes' = ''
  @Input() currency: string = ''
  @Input() isCheckin = false
  @Input() placeholder = 'Select items...'

  // Outputs
  @Output() selectedItemsChanged = new EventEmitter<(number | string)[]>()
  @Output() addClicked = new EventEmitter<void>()
  @Output() tagClicked = new EventEmitter<TagItem>()
  @Output() deleteLanguageClicked = new EventEmitter<Language>()

  // Internal state
  private internalItems: TagItem[] = []
  searchTerm = ''
  private _destroy$ = new Subject<void>()

  constructor(private dialogService: NbDialogService, private promoCodeService: PromoCodeService, private toastrService: ToastrService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initializeItems()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availableItems'] || changes['currentItems']) {
      this.initializeItems()
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Initialize internal items state based on inputs
   */
  private initializeItems(): void {
    if (!this.availableItems?.length) {
      this.internalItems = []
      return
    }

    this.internalItems = this.availableItems.map((item) => ({
      ...item,
      active: this.isItemSelected(item) || (!!item.disabled && !!item.active)
    }))
  }

  /**
   * Check if an item is currently selected
   */
  private isItemSelected(item: TagItem): boolean {
    if (!this.currentItems?.length) return false

    const identifier = this.getItemIdentifier(item)
    return this.currentItems.includes(identifier)
  }

  /**
   * Get the identifier for an item (id or slug based on listType)
   */
  private getItemIdentifier(item: TagItem): number | string {
    return this.listType === 'Languages' ? item.slug : item.id
  }

  /**
   * Get currently displayed tags (selected items) - using original displayedItems logic
   */
  get displayedTags(): TagItem[] {
    if (!this.availableItems?.length) return []

    return this.availableItems.filter((item) => {
      const identifier = this.listType === 'Languages' ? item.slug : item.id
      return this.currentItems.includes(identifier) || (!!item.disabled && !!item.active)
    })
  }

  /**
   * Get filtered available items for the dropdown
   */
  get filteredItems(): TagItem[] {
    if (!this.searchTerm) return this.internalItems

    const term = this.searchTerm.toLowerCase()
    return this.internalItems.filter((item) => {
      const name = item.name || item.code || ''
      return name.toLowerCase().includes(term)
    })
  }

  /**
   * Get workspace languages (custom languages)
   */
  get workspaceLanguages(): TagItem[] {
    if (this.listType !== 'Languages') return []
    return this.filteredItems.filter((item) => this.customLanguages.includes(item.slug))
  }

  /**
   * Get default languages
   */
  get defaultLanguages(): TagItem[] {
    if (this.listType !== 'Languages') return []
    return this.filteredItems.filter((item) => !this.customLanguages.includes(item.slug))
  }

  /**
   * Get currently selected items
   */
  get selectedItems(): (number | string)[] {
    const selected = this.internalItems
      .filter((item) => item.active && !item.separator && (!item.disabled || this.isCheckin))
      .map((item) => this.getItemIdentifier(item))
      .filter((id) => id !== undefined)

    // Add associated selections
    const withAssociated = [...selected]
    this.internalItems
      .filter((item) => item.active && item.alsoSelect)
      .forEach((item) => {
        if (!withAssociated.includes(item.alsoSelect)) {
          withAssociated.push(item.alsoSelect)
        }
      })

    return Array.from(new Set(withAssociated)) // Remove duplicates
  }

  /**
   * Get count of selected items
   */
  get selectedCount(): number {
    return this.displayedTags.length
  }

  /**
   * Toggle item selection
   */
  toggleItem(item: TagItem, value: boolean): void {
    if (item.disabled || item.separator) return

    item.active = value

    // Handle associated selections
    if (item.active && item.alsoSelect) {
      const associatedItem = this.internalItems.find((i) => this.getItemIdentifier(i) === item.alsoSelect)
      if (associatedItem) {
        associatedItem.active = true
      }
    }

    // Handle deselection of items that depend on this one
    if (!item.active) {
      const itemId = this.getItemIdentifier(item)
      this.internalItems.filter((i) => i.alsoSelect === itemId).forEach((i) => (i.active = false))
    }

    this.cdr.markForCheck()
  }

  /**
   * Select all available items
   */
  selectAll(): void {
    this.filteredItems.forEach((item) => {
      if (!item.disabled && !item.separator) {
        item.active = true
      }
    })
    this.cdr.markForCheck()
  }

  /**
   * Clear all selections
   */
  clearAll(): void {
    this.filteredItems.forEach((item) => {
      if (item.disabled || item.separator) return
      item.active = false
    })
    this.cdr.markForCheck()
  }

  /**
   * Apply selections and close popover (original updateSelectedItems logic)
   */
  applySelection(): void {
    if (this.isCheckin && this.selectedItems.length === 0) {
      this.toastrService.error('Error', 'Please select at least one zone')
      return
    }

    this.selectedItemsChanged.emit(this.selectedItems)
    this.popover?.hide()
  }

  /**
   * Handle tag click
   */
  onTagClick(tag: TagItem): void {
    this.tagClicked.emit(tag)
  }

  /**
   * Handle add button click
   */
  onAddClick(): void {
    this.addClicked.emit()
    this.popover?.hide()
  }

  /**
   * Handle language deletion
   */
  onDeleteLanguage(language: TagItem): void {
    this.deleteLanguageClicked.emit(language as Language)
  }

  /**
   * Open promo code creation modal (original openCreateModal logic)
   */
  openCreatePromoCodeModal(): void {
    this.dialogService
      .open(CreatePromoCodeComponent, {
        context: {
          event_id: this.eventId,
          promocodes: this.availableItems,
          currency: this.currency
        },
        closeOnBackdropClick: false
      })
      .onClose.pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.refreshPromoCodes()
      })

    this.popover?.hide()
  }

  /**
   * Refresh promo codes after creation
   */
  private refreshPromoCodes(): void {
    this.promoCodeService
      .getPromoCodes(this.eventId.toString())
      .pipe(takeUntil(this._destroy$))
      .subscribe((newPromoCodes: any[]) => {
        // Preserve active state
        this.availableItems.forEach((promoCode: PromoCode) => {
          const newPromoCode = newPromoCodes.find((o) => o.id === promoCode.id)
          if (newPromoCode) {
            newPromoCode.active = promoCode.active
          }
        })

        // Update available items (this should be handled by parent component)
        // But we update internal state to maintain consistency
        this.internalItems = newPromoCodes.map((item) => ({
          ...item,
          active: this.isItemSelected(item)
        }))

        this.cdr.markForCheck()
      })
  }

  /**
   * Get display text for an item
   */
  getItemDisplayText(item: TagItem): string {
    if (this.listType === 'Languages' && item.slug) {
      return `${item.name} (${item.slug})`
    }
    return item.name || item.code || ''
  }

  /**
   * Track by function for ngFor
   */
  trackByFn = (index: number, item: TagItem): any => {
    return this.getItemIdentifier(item) || index
  }

  /**
   * Handle popover show state change
   */
  public onPopoverToggle(event: { isShown: boolean }): void {
    if (event.isShown) return
    this._resetTheList()
  }

  /**
   * Reset the list of items
   */
  private _resetTheList(): void {
    this.internalItems.forEach((item) => {
      const identifier = this.listType === 'Languages' ? 'slug' : 'id'
      return (item.active = !!this.displayedTags.find((tag) => tag[identifier] === item[identifier]))
    })
    this.searchTerm = ''
  }
}
